import {
  Box,
  Center,
  useColorModeValue,
  Heading,
  Text,
  Stack,
  Image,
  Button,
  Tag,
  Flex,
} from '@chakra-ui/react'
import React, { useEffect, useState } from 'react';
import { BusinessItem } from '../../store/page/types';
import { formatCurrency } from '../global/data/formatCurrencyHelper';
import { Link } from 'react-router-dom';

interface MiniItemCardProps {
  item: BusinessItem;
  businessName?: string;
  xs?: boolean;
}

const MiniItemCard: React.FC<MiniItemCardProps> = ({ item, businessName, xs }) => {
  let initialImage = null;
  if (item && item.pageData && item.pageData.media && item.pageData.media.length > 0) {
    initialImage = item.pageData.media[0];
  }
  if (item && item.associatedBusinesses && item.associatedBusinesses.length > 0) {
    businessName = item.associatedBusinesses[0].name;
  }
  const [image, setImage] = useState(initialImage);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    setCurrentImageIndex(0);
    if (item && item.pageData && item.pageData.media && item.pageData.media.length > 0) {
      initialImage = item.pageData.media[0];
      setImage(initialImage)
    }
  }, [item._id])
  
  useEffect(() => {
    // Start timer to rotate images
    const interval = setInterval(() => {
      const media = item.pageData?.media || [];
      if (image && currentImageIndex + 1 < media.length) {
        setCurrentImageIndex(currentImageIndex + 1);
        setImage(media[currentImageIndex]);
      } else if (image && currentImageIndex + 1 === media.length) {
        setCurrentImageIndex(0);
        setImage(media[0])
      }
    }, 5000);

    // Clear interval on component unmount
    return () => clearInterval(interval);
  }, [item, image, currentImageIndex, initialImage, item._id]);

  const renderPrice = () => {
    const strikeThrough = item?.pageData?.showStrikeThrough;
    const strikeMode = item?.pageData?.strikeThroughMode;
    const strikeNumber = item?.pageData?.strikeThroughNumber;
    if (item && item.currency && item.price && strikeThrough && strikeMode && strikeNumber) {
      let previousPrice: number | null = null;
    
      if (strikeMode === 'amount') {
        previousPrice = item.price + strikeNumber
      } else if (strikeMode === 'percent') {
        const increase = (strikeNumber + 100);
        const change = increase * 0.01;
        previousPrice = item.price * change;
      }    
  
      if (previousPrice && previousPrice !== item.price) {
        return (
          <Stack direction={xs ? 'column' : 'row'} align={'center'}>
            {item.pageData && item.pageData.showStrikeThrough && (
            <Text textDecoration={'line-through'} color={'gray.600'} fontSize={xs ? 'xs' : 'sm'}>
              {formatCurrency(item.currency, previousPrice)}
            </Text>
            )}
            <Text fontWeight={800} color={'green'} fontSize={xs ? 'xs' : 'sm'}>
              {formatCurrency(item.currency, item.price)}
            </Text>
          </Stack>
        )
      }
    }

    if (item && item.currency && item.price) {
      return (
        <Text fontWeight={800} fontSize={xs ? 'xs' : 'sm'}>
          {formatCurrency(item.currency, item.price)}
        </Text>
      )
    }

    return null;
  }
  
  const renderDiscountTags = () => {
    const deliveryDiscount = item.pageData?.deliveryDiscount;
    const deliveryDiscountMode = item.pageData?.deliveryDiscountMode;
    const discount = item.pageData?.discount;
    const discountMode = item.pageData?.discountMode;
    const discountName = item.pageData?.discountName;
    const currency = item.currency || '';
    let discountTags: string[] = [];

    if (deliveryDiscount && deliveryDiscountMode) {
      let text = '';
      if (deliveryDiscount === 100 && deliveryDiscountMode === 'percent') {
        text = 'FREE DELIVERY';
      } else if (deliveryDiscountMode === 'percent') {
        text = `${deliveryDiscount}% OFF DELIVERY`
      } else if (deliveryDiscountMode === 'amount') {
        text = `${formatCurrency(currency, deliveryDiscount)} OFF DELIVERY`
      }
      discountTags = [...discountTags, text]
    }

    if (discount && discountMode) {
      let text = '';
      if (discount === 100 && discountMode === 'percent') {
        text = 'FREE'
      } else if (discountMode === 'percent') {
        text = `${discountName} ${discount}% DISCOUNT`
      } else if (discountMode === 'amount') {
        text = `${discountName} ${formatCurrency(currency, discount)} DISCOUNT`
      }
      discountTags = [...discountTags, text]
    }
    return (
      <Box
        position="absolute"
        bottom={2}
        left={2}
        zIndex={1}
      >
        <Flex flexDirection={'column'}>

        {discountTags.map(text => {
          if (xs) {
            return null;
          } else {
            return (
                    <Button colorScheme='green' key={text} mr={1} mb={1} size={'xs'} opacity={0.9}>
                      <Text fontSize={'xs'}>{text}</Text>
                    </Button>
                  )
          }
        })}
        </Flex>
      </Box>
    )
  }

  return (
    <Link to={`/${item.pageKey}`}>
      <Center py={1}>
        <Box
          role={'group'}
          p={6}
          maxW={xs ? '150px' : '330px'}
          w={'full'}
          bg={useColorModeValue('white', 'gray.800')}
          boxShadow={'sm'}
          rounded={'lg'}
          pos={'relative'}
          zIndex={1}>
          <Box
            rounded={'xl'}
            mt={-2}
            pos={'relative'}
            height={xs ? '100px' : '230px'}
            _after={{
              transition: 'all .3s ease',
              content: '""',
              w: 'full',
              h: 'full',
              pos: 'absolute',
              top: 0,
              left: 0,
              backgroundImage: image ? image.url : false,
              filter: 'blur(5px)',
              zIndex: -1,
            }}
            _groupHover={{
              _after: {
                filter: 'blur(10px)',
              },
            }}>
              {renderDiscountTags()}
            <Image
              rounded={'lg'}
              height={xs ? 100 : 230}
              width={xs ? 122 : 282}
              objectFit={'cover'}
              src={image ? image.url : undefined}
              alt="#"
            />
          </Box>
          <Stack pt={10} align={'center'}>
            <Text color={'gray.500'} fontSize={'sm'} textTransform={'uppercase'}>
              {businessName ? businessName : null}
            </Text>
            <Heading fontSize={xs ? 'xs' : 'md'} fontFamily={'body'} fontWeight={500}>
              {item.name.length > 20 ? `${item.name.slice(0,20)}...` : item.name}
            </Heading>
            {renderPrice()}
          </Stack>
        </Box>
      </Center>
    </Link>
  )
}

export default MiniItemCard;